import moment from 'moment-timezone';
import { ApolloError } from '@apollo/client';
import { BRONCO_SUPPLIER_UUID } from '@/mocks/uuids';
import { formatDate } from '@/utils/date';
import { getDemoObiForBroncoRestaurant } from '@/mocks/dsr-analytics/obi-data';
import { getTranslationValue } from '@/utils/translations';
import { SalesPortalAccountDetailsItemsOfInterestQuery } from '@/types';
import { useGeneratedSalesPortalAccountDetailsItemsOfInterestQuery } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/documents/sales-portal-account-items-of-interest.generated';
import { useMemo } from 'react';

const OBI_LOOKBACK_DAYS = 30;
const MAX_ITEMS = 20;

type ObiItemFromQuery =
  SalesPortalAccountDetailsItemsOfInterestQuery['getObiItems']['obi_items'][number];

export interface ItemOfInterest {
  dateLabel: string;
  imageUrls: string[];
  interactionId: string;
  interactionText: string;
  name: string;
  variantPackUuid: string;
}

interface AccountItemsOfInterestLoaderParams {
  restaurantUUID: string;
  supplierUUID: string;
}

interface AccountItemsOfInterestLoaderResult {
  error?: ApolloError;
  items: ItemOfInterest[] | null;
}

const buildFromDate = (): string => {
  const date = new Date(Date.now());
  date.setDate(date.getDate() - OBI_LOOKBACK_DAYS);
  return date.toISOString().split('T')[0];
};

const localTz = moment.tz.guess();

const transformObiItem = (item: ObiItemFromQuery): ItemOfInterest => ({
  dateLabel:
    formatDate(moment.tz(item.event_time, localTz), {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    }) || '',
  imageUrls: item.variant_pack?.item.photo_url_list ?? [],
  // The same variant pack can appear under multiple interaction types and event times; this composite guarantees per-row uniqueness.
  interactionId: `${item.variant_pack_uuid}-${item.obi_item_type}-${item.event_time}`,
  interactionText: getTranslationValue(item.subtitle),
  name: item.title,
  variantPackUuid: item.variant_pack_uuid,
});

/**
 * Loads items of interest (OBI) for a restaurant-supplier pair from the last 30 days.
 * Returns up to 20 items sorted by most recent event time.
 *
 * When the supplier is Bronco and the restaurant is a Bronco demo account,
 * returns hardcoded mock OBI items instead of querying the backend.
 *
 * @param params.restaurantUUID - The UUID of the restaurant to fetch data for
 * @param params.supplierUUID - The UUID of the supplier
 * @returns Formatted Items of interest for the restaurant from the last 30
 * days, and the query error
 */
export const useAccountItemsOfInterestLoader = ({
  restaurantUUID,
  supplierUUID,
}: AccountItemsOfInterestLoaderParams): AccountItemsOfInterestLoaderResult => {
  const demoItems = useMemo(() => {
    if (supplierUUID === BRONCO_SUPPLIER_UUID) {
      const rawDemoItems = getDemoObiForBroncoRestaurant(restaurantUUID);
      if (!rawDemoItems?.length) {
        return rawDemoItems === undefined ? undefined : [];
      }
      return rawDemoItems
        .slice()
        .sort(
          (a, b) =>
            new Date(b.event_time).getTime() - new Date(a.event_time).getTime(),
        )
        .slice(0, MAX_ITEMS)
        .map(transformObiItem);
    }
  }, [restaurantUUID, supplierUUID]);
  const hasDemoData = demoItems !== undefined;

  const fromDate = useMemo(() => buildFromDate(), []);

  const { data, error } =
    useGeneratedSalesPortalAccountDetailsItemsOfInterestQuery({
      fetchPolicy: 'cache-first',
      skip: hasDemoData,
      variables: {
        fromDate,
        restaurantUUID,
        supplierUUID,
      },
    });

  const items = useMemo(() => {
    if (hasDemoData) {
      return demoItems;
    }

    const rawItems = data?.getObiItems.obi_items;

    if (!rawItems || error) {
      return null;
    }

    return rawItems
      .slice()
      .sort(
        (a, b) =>
          new Date(b.event_time).getTime() - new Date(a.event_time).getTime(),
      )
      .slice(0, MAX_ITEMS)
      .map(transformObiItem);
  }, [data, error, hasDemoData, demoItems]);

  return { error, items };
};
